import { CornerUpLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { formatDateTime, relativeTime } from '../lib/format';
import { Button } from './ui';

/**
 * A workflow's step sent back to someone earlier to check something (operator,
 * 2026-10-15: "everyone on this workflow can return task to previews employee to
 * check for something when this employee finish it will return to the person who
 * ask me"). Its own person returns it from the header; while it is out, the
 * banner says who is checking it, for whom and what — and offers whoever is
 * checking it the way back, and the others who may bring it back theirs.
 */

/** The header's "Return to an earlier person", for the step's own person while they are at it. */
export function TaskReturnAction({ task }: { task: any }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  if (!task.can?.returnStep) return null;
  return (
    <Button icon={<CornerUpLeft className="h-4 w-4 flip-rtl" />} onClick={() => navigate(`/tasks/${task.id}/return`)}>
      {t('tasks.return.action')}
    </Button>
  );
}

export function TaskReturnBanner({ task }: { task: any }) {
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  if (!task.returnToUserId) return null;
  const owner = localized(task.returnTo);
  const checker = localized(task.assignee);
  const checking = task.assigneeUserId === user?.id;
  const asked = task.returnToUserId === user?.id;
  const may = !!task.can?.endCheck;

  const title = checking
    ? t('tasks.return.checkTitle', { name: owner })
    : asked
      ? t('tasks.return.outTitle', { name: checker })
      : t('tasks.return.othersTitle', { checker, owner });
  const body = checking
    ? t('tasks.return.checkBody', { name: owner })
    : asked
      ? t('tasks.return.outBody')
      : t('tasks.return.othersBody', { owner });
  const action = checking
    ? t('tasks.return.done', { name: owner })
    : asked
      ? t('tasks.return.takeBack')
      : t('tasks.return.bringBack', { name: owner });

  return (
    <div className="mb-5 flex flex-wrap items-center gap-4 rounded-lg border border-warning/35 bg-warning/8 p-4">
      <CornerUpLeft className="h-6 w-6 shrink-0 text-warning-fg dark:text-warning-dark flip-rtl" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-title text-[var(--text-primary)]">{title}</p>
        {task.returnNote && (
          // In the words, and the direction, it was written in.
          <p className="mt-1 whitespace-pre-wrap break-words text-body text-[var(--text-primary)]" dir="auto">
            “{task.returnNote}”
          </p>
        )}
        <p className="mt-1 text-body-sm text-muted">
          {body}
          {task.returnedAt && (
            <span className="ms-1 text-caption text-faint" title={formatDateTime(task.returnedAt, locale)}>
              · {t('tasks.return.since', { when: relativeTime(task.returnedAt, locale) })}
            </span>
          )}
        </p>
      </div>
      {may && (
        <Button
          variant={checking ? 'primary' : 'secondary'}
          icon={<CornerUpLeft className="h-4 w-4 flip-rtl" />}
          onClick={() => navigate(`/tasks/${task.id}/return-back`)}
        >
          {action}
        </Button>
      )}
    </div>
  );
}
